import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Flex,
  Heading,
  HStack,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { Subscription } from '../../types'
import { PendingSubscriptionChangeResponse } from '../../types/subscription'
import { BillingClient } from './BillingSlice'
import useApi from '../../common/utils/api/useApi'
import { useAppSelector } from '../../app/typedReduxHooks'
import { RootState } from '../../app/store'
import { upperFirst } from 'lodash'

import { INDIVIDUAL_PLANS, PRICE_PLAN_LABEL, TEAM_PLANS } from './pricePlansData'
import { routes } from '../routes'

export const SubscriptionInfo = ({ subscription }: { subscription: Subscription | undefined }) => {
  const organizationId = useAppSelector((state: RootState) => state.organization?.data?.id)
  const navigate = useNavigate()
  const [scheduledDowngrade, setScheduledDowngrade] =
    useState<PendingSubscriptionChangeResponse | null>(null)

  const { trigger: triggerGetPendingSubscriptionChange } = useApi(
    BillingClient.getPendingSubscriptionChange,
    {
      onSuccess: (result: PendingSubscriptionChangeResponse) => {
        setScheduledDowngrade(result)
      },
      onFailure: () => {
        setScheduledDowngrade(null)
      },
    }
  )

  useEffect(() => {
    if (organizationId) {
      triggerGetPendingSubscriptionChange({ organizationId })
    }
  }, [organizationId])

  const nextPlan =
    scheduledDowngrade?.hasPendingChange &&
    [...INDIVIDUAL_PLANS, ...TEAM_PLANS].find(
      (plan) => plan.pricePlan === scheduledDowngrade.targetPlan
    )

  const { trigger: triggerCreateCustomerPortalSession, loading } = useApi(
    BillingClient.createCustomerPortalSession,
    {
      onSuccess: (checkoutSessionUrl: string) => {
        window.location.href = checkoutSessionUrl
      },
      onFailure: () => {
        if (process.env.REACT_APP_STRIPE_CUSTOMER_PORTAL) {
          window.location.href = process.env.REACT_APP_STRIPE_CUSTOMER_PORTAL
        }
      },
    }
  )

  const handleOpenStripePortal = () => {
    subscription?.stripeSubscriptionId &&
      organizationId &&
      triggerCreateCustomerPortalSession({
        stripeSubscriptionId: subscription.stripeSubscriptionId,
        organizationId,
      })
  }

  const handleUpdateSubscription = () => {
    navigate(routes.pricePlan.path)
  }

  if (!subscription) {
    return null
  }

  const formattedNextBillingDate = subscription.currentPeriodEnd
    ? new Intl.DateTimeFormat('en-AU', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      }).format(new Date(subscription.currentPeriodEnd))
    : null
  const paymentMethodText =
    subscription.paymentDetails?.brand && subscription.paymentDetails?.last4
      ? `${upperFirst(subscription.paymentDetails.brand)} •••• ${subscription.paymentDetails.last4}`
      : 'Updated on Stripe'

  return (
    <VStack spacing={6} align="stretch">
      <VStack spacing={2} align="stretch">
        <Heading size="lg">Manage Subscription</Heading>
        <Text color="gray.600">
          You can manage your subscription and billing information directly from Stripe.
        </Text>
      </VStack>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <Card borderRadius="xl" border="1px" borderColor="gray.200" boxShadow="sm">
          <CardHeader py={4} px={5} bg="gray.50" borderTopRadius="xl">
            <Heading size="md">Plan Details</Heading>
          </CardHeader>
          <CardBody p={5}>
            <VStack spacing={5} align="stretch">
              <SimpleGrid
                columns={scheduledDowngrade?.hasPendingChange ? { base: 1, lg: 2 } : 1}
                spacing={5}
              >
                <VStack spacing={2} align="stretch">
                  {scheduledDowngrade?.hasPendingChange && (
                    <Text fontSize="xs" fontWeight="bold" color="gray.500">
                      Current Plan
                    </Text>
                  )}
                  <Text fontSize="xl" fontWeight="bold" lineHeight="shorter" color="gray.800">
                    {subscription.productName}
                  </Text>
                  <Text fontSize="md" fontWeight="semibold" color="gray.600">
                    {subscription.productPrice ? `${subscription.productPrice} / month` : ''}
                  </Text>
                  <Text color="gray.600">{subscription.seats} seats</Text>
                  <Text color="gray.700">
                    Next Billing Date:{' '}
                    <Text as="span" fontWeight="bold" color="gray.800">
                      {formattedNextBillingDate ?? 'N/A'}
                    </Text>
                  </Text>
                </VStack>

                {scheduledDowngrade?.hasPendingChange && (
                  <VStack
                    spacing={2}
                    align="stretch"
                    borderLeft={{ lg: '1px solid' }}
                    borderLeftColor={{ lg: 'gray.200' }}
                    pl={{ lg: 5 }}
                    pt={{ base: 5, lg: 0 }}
                    borderTop={{ base: '1px solid', lg: 'none' }}
                    borderTopColor={{ base: 'gray.200', lg: 'transparent' }}
                  >
                    <HStack>
                      <Text fontSize="xs" fontWeight="bold" color="orange.600">
                        Scheduled Change
                      </Text>
                    </HStack>
                    <Text fontSize="xl" fontWeight="bold" lineHeight="shorter" color="gray.800">
                      {PRICE_PLAN_LABEL[scheduledDowngrade.targetPlan]}
                    </Text>
                    <Text fontSize="md" fontWeight="semibold" color="gray.600">
                      {nextPlan && nextPlan.price ? `$${nextPlan.price}.00 / month` : ''}
                    </Text>
                    <Text color="gray.600">{scheduledDowngrade.targetSeats} seats</Text>
                    <Text color="gray.700">
                      Effective Date:{' '}
                      <Text as="span" fontWeight="bold" color="gray.800">
                        {new Date(scheduledDowngrade.effectiveAt * 1000).toLocaleDateString(
                          'en-AU',
                          {
                            month: 'long',
                            day: 'numeric',
                            year: 'numeric',
                          }
                        )}
                      </Text>
                    </Text>
                  </VStack>
                )}
              </SimpleGrid>

              <Button
                bgGradient="linear(to-r, blue.500, blue.700)"
                color="white"
                _hover={{ bgGradient: 'linear(to-r, blue.600, blue.800)' }}
                onClick={handleUpdateSubscription}
              >
                Manage Plan & Seats
              </Button>
            </VStack>
          </CardBody>
        </Card>

        <Card borderRadius="xl" border="1px" borderColor="gray.200" boxShadow="sm">
          <CardHeader py={4} px={5} bg="gray.50" borderTopRadius="xl">
            <Heading size="md">Billing & Invoices</Heading>
          </CardHeader>
          <CardBody p={5} display="flex" flexDirection="column">
            <VStack spacing={6} align="stretch" flex="1" justify="space-between">
              <VStack spacing={3} align="stretch">
                <Flex justify="space-between" gap={4}>
                  <Text color="gray.600" fontSize="sm">
                    Default Payment Method:
                  </Text>
                  <Text fontWeight="semibold" color="gray.800" fontSize="sm" textAlign="right">
                    {paymentMethodText}
                  </Text>
                </Flex>
                <Flex justify="space-between" gap={4}>
                  <Text color="gray.600" fontSize="sm">
                    Expires On:
                  </Text>
                  <Text fontWeight="semibold" color="gray.800" fontSize="sm" textAlign="right">
                    {subscription.paymentDetails?.expMonth} / {subscription.paymentDetails?.expYear}
                  </Text>
                </Flex>
              </VStack>

              <Button
                variant="outline"
                borderColor="blue.600"
                color="blue.700"
                _hover={{ bg: 'blue.50' }}
                onClick={handleOpenStripePortal}
                isLoading={loading}
              >
                Manage Billing, Invoices & Cancellation
              </Button>
            </VStack>
          </CardBody>
        </Card>
      </SimpleGrid>
    </VStack>
  )
}
